import type { GeneratedInsight } from "@amzn/d16-g-client-deck-builder-typescript-client";
import {
    Icon,
    Text,
    Button,
    Tooltip,
    Popover,
    TextButton,
} from "@amzn/storm-ui";
import { caretDown, exclamationTriangle, close } from "@amzn/storm-ui-icons";
import {
    useAsync,
    useIntervalEffect,
    useToggle,
    useUnmountEffect,
} from "@react-hookz/web";
import React from "react";
import { useNavigate } from "react-router";
import * as R from "remeda";
import styled from "styled-components";
import { useStore } from "zustand";

import { getCdbApiClient } from "../../api/cdbApi/cdbApi";
import { ADVERTISER_ID_REQUEST_PARAMETER } from "../../config/constants";
import { COPY_MESSAGES } from "../../constants/common";
import { Paths } from "../../constants/paths";
import { TELEMETRY_ID } from "../../constants/telemetry";
import { useGetRequestParameters } from "../../hooks/dimension/dimension.hooks";
import { useGetGeneratedInsightsNotifications } from "../../hooks/generatedInsights/useGetGeneratedInsightsNotifications";
import { useAsyncErrorStatusNotification } from "../../hooks/notifications/useAsyncErrorStatusNotification";
import { dimensionStatusStore } from "../../store/dimension.status";
import { dimensionValueStore } from "../../store/dimension.value";
import { notificationStore, NotificationType } from "../../store/notification";
import { windowStore } from "../../store/window";
import { getStateKeys } from "../../store/zustand.helpers";
import { TabQueryParamValue } from "../../types/navigation";
import { generateShareableUrl, isLoading } from "../../utils/api.utils";
import { copyToClipboard } from "../../utils/clipboard.utils";
import { IHubQueryParam, getAdvertiserId } from "../../utils/permission.utils";
import {
    getFilterTelemetryDetails,
    logWithTakt,
} from "../../utils/telemetry.utils";
import { getSearchParams } from "../../utils/url.utils";
import { CreateInsightDeckReportIssue } from "../aquilaReportIssue/CreateInsightDeckReportIssue";
import { ShareIcon } from "../common/ShareIcon";

const Topbar = styled.div`
    position: sticky;
    top: 0;
    height: 45px;
    padding: 10px 25px;
    background-color: #fbfbfc;
    border-bottom: 2px solid #d9dee4;
    display: flex;
    justify-content: space-between;
    align-items: center;
    z-index: 1;
`;

const TopbarGrouping = styled.div`
    display: flex;
    align-items: center;
    gap: 14px;
`;

const ButtonCollection = styled.div`
    display: flex;
    gap: 3px;
`;

const ButtonGrouped = styled(Button)`
    :first-of-type {
        border-top-right-radius: 0;
        border-bottom-right-radius: 0;
    }
    :only-child {
        border-top-right-radius: 999px;
        border-bottom-right-radius: 999px;
    }
`;

const ButtonGroupedRight = styled(Button)`
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
`;

export interface ActionBarProps {
    domainIdentifier: string;
    templateIdentifier: string;
    templateName: string;
}

export const MAX_REQUEST_PARAMETER_SIZE_IN_BYTES = 100000; // 100KB is the maxmium allowed size we can have for sf payload

export const ActionBar: React.FunctionComponent<ActionBarProps> = ({
    domainIdentifier,
    templateIdentifier,
    templateName,
}) => {
    const [upsertNotification] = useStore(notificationStore, (state) => [
        state.upsertNotification,
    ]);
    const [requestExecutionId, setRequestExecutionId] = React.useState<
        string | undefined
    >();

    const [generatedInsightRequestExecutions, setGeneratedRequestExecutionIds] =
        React.useState<GeneratedInsight[]>([]);

    const advertiserId = getAdvertiserId();

    const [
        shouldNavigateAwayWhenExecutionVerified,
        setNavigateAwayWhenExecutionVerified,
    ] = React.useState<boolean>(false);

    const [isCreating, setIsCreating] = React.useState<boolean>(false);

    const [dimensionsIds] = useStore(dimensionValueStore, (state) => [
        getStateKeys(state),
    ]);

    const [dimensionStatusSuccess] = useStore(dimensionStatusStore, (state) => [
        state.checkAllStatusSuccess(),
    ]);

    const [pathname] = useStore(windowStore, (state) => [state.pathname]);

    const [dismissNotifications] = useStore(notificationStore, (state) => [
        state.dismissNotification,
    ]);

    const currentRequestParameters = useGetRequestParameters(dimensionsIds);

    const navigate = useNavigate();

    const intervalRefreshRateMS = 2000; // 2 seconds;

    const [setData, nameDimension] = useStore(dimensionValueStore, (state) => [
        state.setData,
        state["name"],
    ]);

    const [generationState, generationAction] = useAsync(async () => {
        if (requestExecutionId) {
            const { data } = await (
                await getCdbApiClient()
            ).getGeneratedInsight({
                advertiserId,
                executionId: requestExecutionId,
            });
            setIsCreating(false);
            if (shouldNavigateAwayWhenExecutionVerified) {
                navigate(
                    `${Paths.DISCOVERY}${getSearchParams({ [IHubQueryParam.INITIAL_TAB]: TabQueryParamValue.GENERATED_INSIGHTS }, ["getPayloadFromInsight", IHubQueryParam.INITIAL_TAB])}`,
                );
            } else {
                if (data) {
                    setGeneratedRequestExecutionIds((prev) => [...prev, data]);
                    setRequestExecutionId(undefined);

                    if (nameDimension) {
                        setData("name", { value: "" }, {});
                    }
                }
            }
        }
    });
    useAsyncErrorStatusNotification(generationState, ["NotFoundException"]);

    useIntervalEffect(() => {
        if (isCreating) {
            void generationAction.execute();
        }
    }, intervalRefreshRateMS);

    const [packagedInsightState, packagedInsightAction] = useAsync(async () => {
        const data = await (
            await getCdbApiClient()
        ).createPackagedInsight({
            advertiserId,
            domainIdentifier,
            templateIdentifier,
            parameters: R.merge(
                ADVERTISER_ID_REQUEST_PARAMETER,
                currentRequestParameters,
            ),
            origin: `${origin}${pathname}${getSearchParams({ [IHubQueryParam.INITIAL_TAB]: TabQueryParamValue.GENERATED_INSIGHTS }, [IHubQueryParam.INITIAL_TAB])}`,
        });

        setRequestExecutionId(data.executionId);
        setIsCreating(true);
        void generationAction.execute();
        logWithTakt(TELEMETRY_ID.CUSTOM_INSIGHT_REQUEST, {
            executionId: data.executionId!,
            ...getFilterTelemetryDetails(),
        });
    });
    useAsyncErrorStatusNotification(packagedInsightState);

    const requestParametersSizeInBytes = React.useMemo(
        () => new Blob([JSON.stringify(currentRequestParameters)]).size,
        [currentRequestParameters],
    );

    const [generateOptionsPopover, toggleGenerateOptionsPopover] = useToggle();

    useGetGeneratedInsightsNotifications(generatedInsightRequestExecutions);

    useUnmountEffect(() => {
        generatedInsightRequestExecutions.forEach((generatedInsight) => {
            dismissNotifications(generatedInsight.identifier!);
        });
    });

    const handleShareClick = () => {
        const baseUrl = window.location.origin + window.location.pathname;
        const templateId = templateIdentifier;

        if (!templateId) {
            upsertNotification(
                "Unable to share template: Template ID is missing",
                NotificationType.ERROR,
            );
            return;
        }

        const urlParams: Record<string, string> = {};

        // Check if sfId exists in current URL
        const searchParams = new URLSearchParams(window.location.search);
        const sfId = searchParams.get("sfId");

        if (sfId) {
            urlParams.sfId = sfId;
        }

        const shareUrl = generateShareableUrl(baseUrl, urlParams);

        copyToClipboard(
            shareUrl,
            "share-url",
            COPY_MESSAGES.TEMPLATE || "Template URL copied to clipboard",
        );

        logWithTakt(TELEMETRY_ID.INSIGHT_SHARE_SUCCESS, {
            templateId,
            shareType: "single",
        });
    };

    const isSamePayload = React.useMemo(() => {
        const currentPayload = R.merge(
            ADVERTISER_ID_REQUEST_PARAMETER,
            currentRequestParameters,
        );
        for (const generatedInsight of generatedInsightRequestExecutions) {
            if (
                generatedInsight.metadata &&
                R.isDeepEqual(
                    R.omit(
                        generatedInsight.metadata as Record<string, string>,
                        ["name"],
                    ),
                    R.omit(currentPayload as Record<string, string>, ["name"]),
                )
            )
                return true;
        }
        return false;
    }, [
        currentRequestParameters,
        ADVERTISER_ID_REQUEST_PARAMETER,
        generatedInsightRequestExecutions,
    ]);

    const isSameNarrativeName = React.useMemo(() => {
        const currentPayload = R.merge(
            ADVERTISER_ID_REQUEST_PARAMETER,
            currentRequestParameters,
        );
        for (const generatedInsight of generatedInsightRequestExecutions) {
            if (
                currentPayload["name"] &&
                R.isDeepEqual(generatedInsight.title, currentPayload["name"])
            ) {
                return true;
            }
        }
        return false;
    }, [
        currentRequestParameters,
        ADVERTISER_ID_REQUEST_PARAMETER,
        generatedInsightRequestExecutions,
    ]);

    const errorMessages = React.useMemo(() => {
        const messages: string[] = [];
        if (
            requestParametersSizeInBytes > MAX_REQUEST_PARAMETER_SIZE_IN_BYTES
        ) {
            messages.push(
                "Request parameters size exceeds the maximum allowed size of 100KB; You must reduce the amount of inputs selected to proceed",
            );
        }

        if (isSamePayload) {
            messages.push(
                "An insight with these inputs has been generated rapid repeat, please modify the inputs to generate another insight",
            );
        }

        if (isSameNarrativeName) {
            messages.push(
                "Another insight with this insight name has been generated by rapid repeat, please update insight name",
            );
        }

        if (messages.length > 0) {
            return (
                <div>
                    {messages.map((message) => (
                        <p>{message}</p>
                    ))}
                </div>
            );
        } else {
            return undefined;
        }
    }, [requestParametersSizeInBytes, isSamePayload, isSameNarrativeName]);

    const disableGenerateInsightDeckButton = !(
        dimensionStatusSuccess && !errorMessages
    );

    const exitPath = `${Paths.DISCOVERY}${getSearchParams({}, ["getPayloadFromInsight"])}`;

    const rapidRepeatClickHandler = () => {
        setNavigateAwayWhenExecutionVerified(false);
        toggleGenerateOptionsPopover();
        void packagedInsightAction.execute();
        logWithTakt(TELEMETRY_ID.IHUB_CUSTOM_INSIGHTS_RAPID_REPEAT, {
            domainIdentifier,
            templateIdentifier,
        });
    };
    return (
        <Topbar>
            <TopbarGrouping>
                <Icon
                    data-testid="create-insight-action-exit"
                    type={close}
                    onClick={() => navigate(exitPath)}
                    style={{ cursor: "pointer" }}
                />
                <Text renderAs="span" size="large">
                    {templateName}
                </Text>
                <ShareIcon
                    onClick={handleShareClick}
                    tooltipMessage="Share template"
                    disabled={false}
                    testId="share-template-button"
                />
            </TopbarGrouping>
            <TopbarGrouping>
                <Button
                    variant="transparent"
                    data-testid="create-insight-cancel"
                    onClick={() => navigate(exitPath)}
                >
                    Cancel
                </Button>
                {errorMessages && (
                    <div>
                        <Tooltip
                            trigger={
                                <Icon
                                    data-testid="action-bar-error-messages-tooltip"
                                    type={exclamationTriangle}
                                    style={{ color: "red" }}
                                />
                            }
                            position="bottom"
                            withCloseButton={false}
                            message={errorMessages}
                        />
                    </div>
                )}
                <ButtonCollection>
                    <ButtonGrouped
                        data-testid="create-packaged-insight"
                        type="submit"
                        variant="primary"
                        style={{
                            border: (() =>
                                errorMessages ? "1px solid red" : "inherit")(),
                        }}
                        loading={
                            isLoading(packagedInsightState.status) ||
                            isLoading(generationState.status) ||
                            isCreating
                        }
                        onClick={() => {
                            setNavigateAwayWhenExecutionVerified(true);
                            void packagedInsightAction.execute();
                        }}
                        disabled={disableGenerateInsightDeckButton}
                    >
                        Generate Insight Deck
                    </ButtonGrouped>
                    <Popover
                        id="example-popper"
                        position="bottom"
                        isOpen={generateOptionsPopover}
                        withCloseButton={false}
                        autoFocus={true}
                        onCloseButtonClick={() => {
                            toggleGenerateOptionsPopover();
                        }}
                        trigger={
                            <ButtonGroupedRight
                                variant="primary"
                                data-testid="create-packaged-insight-options"
                                onClick={() => toggleGenerateOptionsPopover()}
                                disabled={
                                    disableGenerateInsightDeckButton ||
                                    isLoading(packagedInsightState.status) ||
                                    isLoading(generationState.status) ||
                                    isCreating
                                }
                            >
                                <Icon type={caretDown} />
                            </ButtonGroupedRight>
                        }
                    >
                        <TextButton
                            data-testid="package-rapid-repeat"
                            onClick={rapidRepeatClickHandler}
                        >
                            Rapid Repeat
                        </TextButton>
                    </Popover>
                </ButtonCollection>
                <CreateInsightDeckReportIssue />
            </TopbarGrouping>
        </Topbar>
    );
};
